
import React, { useState } from 'react';
import { CodeEditorField, Card, Button, TextField, IconButton } from '@dopomogai/ui';
import { Code, Copy, CheckCircle, Globe, Trash2, Plus, Download, RefreshCw, AlertCircle } from 'lucide-react';

interface DeployTabContentProps {
  embedScriptContent?: string | null;
  embedError?: string | null;
  embedLoading?: boolean;
  onEmbedRetry?: () => void;
  canRetryEmbed?: boolean;
  hasActiveVersion?: boolean;
  isTemplateDisabled?: boolean;
  allowedOrigins: string[];
  onOriginsUpdate: (origins: string[]) => void;
  onEmbedCopy?: () => void;
  onEmbedDownload?: () => void;
}

export const DeployTabContent: React.FC<DeployTabContentProps> = ({
  embedScriptContent,
  embedError,
  embedLoading,
  onEmbedRetry,
  canRetryEmbed = false,
  hasActiveVersion = false,
  isTemplateDisabled = false,
  allowedOrigins,
  onOriginsUpdate,
  onEmbedCopy,
  onEmbedDownload,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [newOrigin, setNewOrigin] = useState('');
  const [originError, setOriginError] = useState<string | null>(null);

  const hasEmbedScript = Boolean(embedScriptContent && embedScriptContent.trim().length > 0);
  const canRetry = Boolean(onEmbedRetry) && !embedLoading && canRetryEmbed;
  const showEmbedEmptyState = !hasEmbedScript && !embedLoading;

  const validateOrigin = (input: string): { normalized: string | null; error: string | null } => {
    const trimmed = input.trim();
    if (!trimmed) return { normalized: null, error: null };
    if (/^https?:\/\//i.test(trimmed)) {
      return { normalized: null, error: 'Use hostname only (no http:// or https://).' };
    }
    if (/[*]/.test(trimmed)) {
      return { normalized: null, error: 'Wildcard origins are not supported.' };
    }
    if (/[/?#]/.test(trimmed)) {
      return { normalized: null, error: 'Do not include path, query, or hash.' };
    }
    if (/\s/.test(trimmed)) {
      return { normalized: null, error: 'Origin cannot contain spaces.' };
    }

    const parts = trimmed.split(':');
    if (parts.length > 2) return { normalized: null, error: 'Invalid origin format.' };
    const host = parts[0].toLowerCase();
    const port = parts[1];

    const isLocalhost = host === 'localhost';
    const isIPv4 = /^(\d{1,3}\.){3}\d{1,3}$/.test(host);
    const isHostname = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/i.test(host);
    if (!isLocalhost && !isIPv4 && !isHostname) {
      return { normalized: null, error: 'Enter a valid hostname, localhost, or IPv4 address.' };
    }

    if (port) {
      if (!/^\d{1,5}$/.test(port)) {
        return { normalized: null, error: 'Port must be numeric.' };
      }
      const portNum = Number(port);
      if (portNum < 1 || portNum > 65535) {
        return { normalized: null, error: 'Port must be between 1 and 65535.' };
      }
    }

    const normalized = port ? `${host}:${port}` : host;
    return { normalized, error: null };
  };

  const liveValidation = validateOrigin(newOrigin);
  const addDisabled = !newOrigin.trim() || Boolean(liveValidation.error);

  const extractMountId = (script: string | undefined) => {
    if (!script) return null;
    const match = script.match(/data-mount-id=["']([^"']+)["']/i);
    return match?.[1] ?? null;
  };

  const buildDownloadContent = (script: string | undefined) => {
    if (!script) return '';
    const mountId = extractMountId(script) || 'dopomogai-widget-mount-point';
    return `<!-- Dopomogai Widget Embed -->
<div id="${mountId}"></div>
${script}`;
  };

  const handleCopyCode = async () => {
    if (!hasEmbedScript) return;
    try {
      await navigator.clipboard.writeText(embedScriptContent as string);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
      onEmbedCopy?.();
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const handleDownload = () => {
    if (!hasEmbedScript) return;
    const content = buildDownloadContent(embedScriptContent as string);
    const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'dopomogai-widget-embed.html';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    onEmbedDownload?.();
  };

  const handleAddOrigin = () => {
    const { normalized, error } = validateOrigin(newOrigin);
    if (error || !normalized) {
      setOriginError(error ?? 'Origin is required.');
      return;
    }
    const duplicate = allowedOrigins.some((origin) => origin.toLowerCase() === normalized.toLowerCase());
    if (duplicate) {
      setOriginError('Origin already exists.');
      return;
    }
    onOriginsUpdate([...allowedOrigins, normalized]);
    setNewOrigin('');
    setOriginError(null);
  };

  const handleRemoveOrigin = (originToRemove: string) => {
    onOriginsUpdate(allowedOrigins.filter(origin => origin !== originToRemove));
  };

  const displayScript = hasEmbedScript
    ? (embedScriptContent as string)
    : '// Save and activate your widget to generate the embed code.';

  return (
    <div className="space-y-6" data-testid="widget-deploy-tab">
      <Card
        title="Embed Code"
        icon={<Code className="w-5 h-5" />}
        noPadding={false}
        headerBgColor="bg-indigo-100"
        actionButton={
          <div className="flex items-center gap-2 flex-nowrap whitespace-nowrap">
            {onEmbedRetry && (
              <Button
                variant="outline"
                size="sm"
                icon={<RefreshCw className="w-4 h-4" />}
                text={embedLoading ? 'Loading...' : 'Retry'}
                onClick={onEmbedRetry}
                disabled={!canRetry}
                className="shrink-0"
                data-testid="widget-deploy-embed-retry"
              />
            )}
            <Button
              variant="outline"
              size="sm"
              icon={<Download className="w-4 h-4" />}
              text="Download"
              onClick={handleDownload}
              disabled={!hasEmbedScript}
              className="shrink-0"
              data-testid="widget-deploy-embed-download"
            />
            <Button
              variant="outline"
              size="sm"
              icon={copySuccess ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              text={copySuccess ? 'Copied!' : 'Copy Code'}
              onClick={handleCopyCode}
              disabled={!hasEmbedScript}
              className="shrink-0"
              data-testid="widget-deploy-embed-copy"
            />
          </div>
        }
      >
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="widget-deploy-checklist">
          <p className="text-xs font-semibold text-slate-700 mb-2">Deployment Checklist</p>
          <ul className="space-y-1 text-xs text-slate-600">
            <li className={hasActiveVersion ? 'text-emerald-700' : ''}>
              {hasActiveVersion ? '✓' : '○'} Save & activate a widget version
            </li>
            <li className={allowedOrigins.length > 0 ? 'text-emerald-700' : ''}>
              {allowedOrigins.length > 0 ? '✓' : '○'} Add at least one allowed origin
            </li>
            <li className={hasEmbedScript ? 'text-emerald-700' : ''}>
              {hasEmbedScript ? '✓' : '○'} Copy or download embed snippet
            </li>
          </ul>
        </div>

        <p className="text-sm text-slate-600 mb-3">
          Add this script just before the closing <code>&lt;/body&gt;</code> tag on any page you want the widget to appear.
          In single-page apps (React/Vite), add it once to your <code>index.html</code>. The widget will load automatically when the page is viewed.
          Make sure your domain is listed in “Allowed origins.” You only need one embed per page.
        </p>
        {showEmbedEmptyState && (
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            {!hasActiveVersion
              ? 'Embed is unavailable until you Save & Activate at least one version.'
              : embedError || 'Embed is currently unavailable. Use Retry to fetch the latest script.'}
            {isTemplateDisabled && (
              <div className="mt-1">
                This template is currently disabled. Public sessions are blocked until enabled.
              </div>
            )}
          </div>
        )}
        <div data-testid="widget-deploy-embed-code">
          <CodeEditorField
            value={displayScript}
            onChange={() => {}}
            language="html"
            readOnly
          />
        </div>
        {embedError && (
          <p className="mt-3 text-xs text-rose-600 flex items-start gap-1">
            <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>{embedError}</span>
          </p>
        )}
      </Card>

      <Card
        title="Allowed Origins"
        icon={<Globe className="w-5 h-5" />}
        noPadding={false}
        headerBgColor="bg-lime-100"
      >
        <p className="text-sm text-slate-600 mb-4">
          Specify the domains where this widget is allowed to be embedded. Use <code>localhost</code> for local testing.
        </p>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <TextField
              label="Add Origin"
              value={newOrigin}
              onChange={(e) => {
                setNewOrigin(e.target.value);
                setOriginError(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && !addDisabled && handleAddOrigin()}
              placeholder="example.com"
              data-testid="widget-deploy-origin-input"
            />
            <Button
              text="Add"
              icon={<Plus size={16} />}
              onClick={handleAddOrigin}
              disabled={addDisabled}
              data-testid="widget-deploy-origin-add"
            />
          </div>
          <p className="text-xs text-slate-500 -mt-1">
            Hostname only. Example: <code>example.com</code> or <code>localhost:5173</code>.
          </p>
          {(originError || liveValidation.error) && (
            <p className="text-xs text-rose-600" data-testid="widget-deploy-origin-error">
              {originError || liveValidation.error}
            </p>
          )}

          <div className="space-y-2" data-testid="widget-deploy-origin-list">
            {allowedOrigins.length > 0 ? (
              allowedOrigins.map(origin => (
                <div
                  key={origin}
                  className="flex items-center justify-between p-3 bg-slate-50 border-2 border-black rounded-lg"
                  data-testid={`widget-deploy-origin-item-${origin}`}
                >
                  <span className="font-mono text-sm text-slate-700">{origin}</span>
                  <IconButton
                    icon={<Trash2 size={16} />}
                    ariaLabel={`Remove ${origin}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveOrigin(origin)}
                  />
                </div>
              ))
            ) : (
              <p className="text-center text-sm text-slate-500 py-4">
                No origins added yet.
              </p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
